import React, { useState, useEffect } from "react";
import AdminNavBar from "../components/AdminNavbar";
import "../assets/css/apppay-reports.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalApplicants: 0,
    totalPaid: 0,
    totalPending: 0,
    totalProgrammes: 0,
    programmeCounts: [], // [{ prog, count }]
    monthlyTrend: [], // [{ month, applicants }]
    paymentDistribution: { paid: 0, pending: 0, other: 0 },
  });

  useEffect(() => {
    fetchReportsData();
  }, []);

  const fetchReportsData = async () => {
    setLoading(true);
    try {
      // ----- In production, replace with real API calls -----
      // 1. Fetch all applications for counts
      const appRes = await fetch(`${API_BASE_URL}/api/checkapplicationinfo`);
      const appData = await appRes.json();
      const applications = Array.isArray(appData)
        ? appData
        : appData.data || [];

      // 2. Fetch programme counts (or compute from applications)
      // For demo, we'll compute from applications
      const progMap = new Map();
      applications.forEach((app) => {
        const prog = app.programme || "Unknown";
        progMap.set(prog, (progMap.get(prog) || 0) + 1);
      });
      const programmeCounts = Array.from(progMap.entries())
        .map(([prog, count]) => ({ prog, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8); // top 8

      // 3. Payment summary
      let paid = 0,
        pending = 0,
        other = 0;
      applications.forEach((app) => {
        const status = (app.paymentStatus || "").toLowerCase();
        if (status.includes("paid")) paid++;
        else if (status.includes("pending") || status.includes("unpaid"))
          pending++;
        else other++;
      });

      // 4. Monthly trend (last 6 months)
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = d.toLocaleString("default", { month: "short" });
        months.push({ month: monthStr, applicants: 0 });
      }
      applications.forEach((app) => {
        if (!app.createdAt) return;
        const date = new Date(app.createdAt);
        const monthYear = date.toLocaleString("default", { month: "short" });
        const idx = months.findIndex((m) => m.month === monthYear);
        if (idx !== -1) months[idx].applicants++;
      });

      setStats({
        totalApplicants: applications.length,
        totalPaid: paid,
        totalPending: pending,
        totalProgrammes: programmeCounts.length,
        programmeCounts,
        monthlyTrend: months,
        paymentDistribution: { paid, pending, other },
      });
    } catch (error) {
      console.error("Failed to fetch reports data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Max count for programme bar chart scaling
  const maxProgrammeCount = stats.programmeCounts.length
    ? Math.max(...stats.programmeCounts.map((p) => p.count))
    : 1;

  // Max count for monthly trend scaling
  const maxMonthlyCount = stats.monthlyTrend.length
    ? Math.max(...stats.monthlyTrend.map((m) => m.applicants), 1)
    : 1;

  return (
    <div className="apppay-reports-wrapper">
      <AdminNavBar />
      <div className="apppay-reports-page">
        <main className="apppay-reports-content">
          {/* Header */}
          <div className="apppay-reports-header">
            <h1 className="apppay-reports-title">Reports & Analytics</h1>
            <div className="apppay-reports-actions">
              <button
                className="apppay-btn apppay-btn-outline"
                onClick={fetchReportsData}
              >
                ↻ Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="apppay-reports-loading">Loading reports…</div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="apppay-kpi-grid">
                <div className="apppay-kpi-card">
                  <span className="apppay-kpi-label">Total Applicants</span>
                  <span className="apppay-kpi-value">
                    {stats.totalApplicants}
                  </span>
                </div>
                <div className="apppay-kpi-card">
                  <span className="apppay-kpi-label">Paid</span>
                  <span className="apppay-kpi-value">{stats.totalPaid}</span>
                  <span className="apppay-kpi-trend">
                    {stats.totalApplicants
                      ? (
                          (stats.totalPaid / stats.totalApplicants) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </span>
                </div>
                <div className="apppay-kpi-card">
                  <span className="apppay-kpi-label">Pending</span>
                  <span className="apppay-kpi-value">{stats.totalPending}</span>
                  <span className="apppay-kpi-trend">
                    {stats.totalApplicants
                      ? (
                          (stats.totalPending / stats.totalApplicants) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </span>
                </div>
                <div className="apppay-kpi-card">
                  <span className="apppay-kpi-label">Programmes</span>
                  <span className="apppay-kpi-value">
                    {stats.totalProgrammes}
                  </span>
                </div>
              </div>

              {/* Two‑column layout */}
              <div className="apppay-reports-grid">
                {/* Left column: Top programmes bar chart */}
                <div className="apppay-report-card">
                  <div className="apppay-report-card-header">
                    <h3>Top Programmes by Applicants</h3>
                  </div>
                  <div className="apppay-bar-list">
                    {stats.programmeCounts.length === 0 ? (
                      <div className="apppay-empty">No data</div>
                    ) : (
                      stats.programmeCounts.map((item) => (
                        <div key={item.prog} className="apppay-bar-row">
                          <span className="apppay-bar-label" title={item.prog}>
                            {item.prog}
                          </span>
                          <div className="apppay-bar-container">
                            <div
                              className="apppay-bar-fill"
                              style={{
                                width: `${(item.count / maxProgrammeCount) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="apppay-bar-count">{item.count}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Right column: Payment distribution & summary */}
                <div className="apppay-report-card">
                  <div className="apppay-report-card-header">
                    <h3>Payment Status</h3>
                  </div>
                  <div className="apppay-payment-distribution">
                    <div className="apppay-donut-container">
                      {/* Simple donut with conic-gradient */}
                      <div
                        className="apppay-donut"
                        style={{
                          background: `conic-gradient(
                            var(--apppay-primary) 0% ${
                              (stats.paymentDistribution.paid /
                                stats.totalApplicants) *
                              100
                            }%,
                            #ffb74d ${
                              (stats.paymentDistribution.paid /
                                stats.totalApplicants) *
                              100
                            }% ${
                              ((stats.paymentDistribution.paid +
                                stats.paymentDistribution.pending) /
                                stats.totalApplicants) *
                              100
                            }%,
                            #e0e0e0 ${
                              ((stats.paymentDistribution.paid +
                                stats.paymentDistribution.pending) /
                                stats.totalApplicants) *
                              100
                            }% 100%
                          )`,
                        }}
                      >
                        <div className="apppay-donut-hole">
                          <span>{stats.totalApplicants}</span>
                          <small>total</small>
                        </div>
                      </div>
                    </div>
                    <div className="apppay-legend">
                      <div className="apppay-legend-item">
                        <span className="apppay-legend-color paid" />
                        <span>Paid</span>
                        <strong>{stats.paymentDistribution.paid}</strong>
                      </div>
                      <div className="apppay-legend-item">
                        <span className="apppay-legend-color pending" />
                        <span>Pending</span>
                        <strong>{stats.paymentDistribution.pending}</strong>
                      </div>
                      <div className="apppay-legend-item">
                        <span className="apppay-legend-color other" />
                        <span>Other</span>
                        <strong>{stats.paymentDistribution.other}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom row: Monthly trend */}
              <div className="apppay-report-card apppay-trend-card">
                <div className="apppay-report-card-header">
                  <h3>Applicants per Month (last 6 months)</h3>
                </div>
                <div className="apppay-trend-chart">
                  {stats.monthlyTrend.map((item) => (
                    <div key={item.month} className="apppay-trend-column">
                      <div className="apppay-trend-bar-container">
                        <div
                          className="apppay-trend-bar"
                          style={{
                            height: `${(item.applicants / maxMonthlyCount) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="apppay-trend-label">{item.month}</span>
                      <span className="apppay-trend-value">
                        {item.applicants}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
